import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {Button, Dialog, DialogBody, Spinner, Tab, Tabs, Tag} from '@blueprintjs/core'
import {InputGroup, Switch} from '@blueprintjs/core'
import styled from 'styled-components';

import {Table, Row, Cell} from '/src/components/tables/table'


// ----- Network Info Dialog -----
const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 6px 16px;
    align-items: center;
    font-size: 13px;
`;

const InfoLabel = styled.span`
    font-weight: 600;
    color: #5c7080;
    white-space: nowrap;
`;

const InfoValue = styled.span`
    font-family: monospace;
    word-break: break-all;
`;

const SectionTitle = styled.div`
    grid-column: 1 / -1;
    font-weight: 700;
    font-size: 14px;
    margin-top: 12px;
    padding-bottom: 4px;
    border-bottom: 1px solid #e1e8ed;
    &:first-child { margin-top: 0; }
`;

const InfoRow = ({label, value}) => value !== undefined && value !== null && value !== '' ? (
    <><InfoLabel>{label}</InfoLabel><InfoValue>{String(value)}</InfoValue></>
) : null;

const ActiveConnectionInfo = ({active}) => {
    if (!active || !Object.keys(active).length) return <Tag minimal>No active connection</Tag>;

    const ip4Config = active.Ip4Config || active.Ip4;
    const connectionSettings = active.Connection;

    return (
        <InfoGrid>
            {connectionSettings?.connection && <>
                <SectionTitle>Connection</SectionTitle>
                <InfoRow label="Name" value={connectionSettings.connection.id} />
                <InfoRow label="UUID" value={connectionSettings.connection.uuid} />
                <InfoRow label="Type" value={connectionSettings.connection.type} />
                <InfoRow label="Interface" value={connectionSettings.connection['interface-name']} />
                <InfoRow label="Autoconnect" value={connectionSettings.connection.autoconnect} />
            </>}

            {connectionSettings?.ipv4 && <>
                <SectionTitle>IPv4 Configuration</SectionTitle>
                <InfoRow label="Method" value={connectionSettings.ipv4.method} />
                {connectionSettings.ipv4.addresses?.map((addr, i) => (
                    <Fragment key={i}>
                        <InfoLabel>{i === 0 ? 'Address' : `Address ${i+1}`}</InfoLabel>
                        <InfoValue>{addr[0]} /{addr[1]} via {addr[2]}</InfoValue>
                    </Fragment>
                ))}
                {connectionSettings.ipv4.dns?.length > 0 &&
                    <InfoRow label="DNS" value={connectionSettings.ipv4.dns.join(', ')} />
                }
                <InfoRow label="Route Metric" value={connectionSettings.ipv4['route-metric']} />
            </>}

            {ip4Config && <>
                <SectionTitle>Active State</SectionTitle>
                {ip4Config.AddressData?.map((addr, i) => (
                    <Fragment key={i}>
                        <InfoLabel>{i === 0 ? 'Address' : `Address ${i+1}`}</InfoLabel>
                        <InfoValue>{addr.address}{addr.prefix ? `/${addr.prefix}` : ''}</InfoValue>
                    </Fragment>
                ))}
                {ip4Config.RouteData?.map((route, i) => (
                    <Fragment key={i}>
                        <InfoLabel>{i === 0 ? 'Route' : `Route ${i+1}`}</InfoLabel>
                        <InfoValue>
                            {route.dest}{route.prefix ? `/${route.prefix}` : ''}
                            {route['next-hop'] ? ` via ${route['next-hop']}` : ''}
                            {route.metric !== undefined ? ` metric ${route.metric}` : ''}
                        </InfoValue>
                    </Fragment>
                ))}
                {ip4Config.Options && Object.entries(ip4Config.Options).map(([key, val]) => (
                    <InfoRow key={key} label={key} value={Array.isArray(val) ? val.join(', ') : val} />
                ))}
            </>}
        </InfoGrid>
    );
};

const AvailableConnectionsList = ({connections}) => {
    if (!connections?.length) return <Tag minimal>No available connections</Tag>;
    return (
        <InfoGrid>
            {connections.map((con, i) => (
                <Fragment key={i}>
                    <InfoLabel>{con.connection?.id || `Connection ${i+1}`}</InfoLabel>
                    <InfoValue>
                        {con.ipv4?.method || '—'}
                        {con.ipv4?.addresses?.[0] ? ` — ${con.ipv4.addresses[0][0]}` : ''}
                    </InfoValue>
                </Fragment>
            ))}
        </InfoGrid>
    );
};

const AdapterPanel = ({adapterInfo}) => {
    if (!adapterInfo) return null;
    return (
        <div style={{padding: '8px 0'}}>
            <div style={{marginBottom: 8}}>
                <Tag minimal intent="primary">{adapterInfo.type}</Tag>
            </div>
            <ActiveConnectionInfo active={adapterInfo.active_connection} />
            {adapterInfo.available_connections?.length > 0 && <>
                <div style={{marginTop: 16}}>
                    <SectionTitle>Available Connections ({adapterInfo.available_connections.length})</SectionTitle>
                </div>
                <AvailableConnectionsList connections={adapterInfo.available_connections} />
            </>}
        </div>
    );
};

const NetworkInfoDialog = ({isOpen, onClose, adapters, loading, error}) => {
    // Sort adapters so ones with an active connection come first
    const sortedEntries = adapters ? Object.entries(adapters).sort(([, a], [, b]) => {
        const aActive = a.active_connection && Object.keys(a.active_connection).length > 0;
        const bActive = b.active_connection && Object.keys(b.active_connection).length > 0;
        return (bActive ? 1 : 0) - (aActive ? 1 : 0);
    }) : [];

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            title="Network Info"
            icon="ip-address"
            style={{width: 550}}
        >
            <DialogBody>
                {loading && <Spinner size={30} />}
                {error && <Tag intent="danger" large fill>{error}</Tag>}
                {sortedEntries.length > 0 && !loading && (
                    <Tabs defaultSelectedTabId={sortedEntries[0]?.[0]}>
                        {sortedEntries.map(([ifname, info]) => (
                            <Tab
                                key={ifname}
                                id={ifname}
                                title={ifname}
                                panel={<AdapterPanel adapterInfo={info} />}
                            />
                        ))}
                    </Tabs>
                )}
            </DialogBody>
        </Dialog>
    );
};


const _validIp = (value) => {
    if (!value) return true;
    const parts = value.split('.');
    if (parts.length !== 4) return false;
    return parts.every(p => /^\d{1,3}$/.test(p) && Number(p) >= 0 && Number(p) <= 255);
};

const _validNetmask = (value) => {
    if (!value) return true;
    if (!_validIp(value)) return false;
    // valid netmask is a contiguous run of 1-bits followed by 0-bits
    const num = value.split('.').reduce((acc, octet) => (acc << 8) | Number(octet), 0) >>> 0;
    if (num === 0) return false;
    const inverted = ~num >>> 0;
    return (inverted & (inverted + 1)) === 0;
};

// ----- Network Control -----
const NetworkControl = ({onSubmit, onGetInfo}) => {
    const [advanced, setAdvanced] = useState(false);
    const [config, setConfig] = useState({});
    const [configuring, setConfiguring] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [adapters, setAdapters] = useState(null);
    const [infoLoading, setInfoLoading] = useState(false);
    const [infoError, setInfoError] = useState(null);

    useEffect(() => {
        setConfig({
            ...config,
            gateway: "",
            dns: "",
        });
    }, [advanced])

    const ipValid = _validIp(config.ip);
    const netmaskValid = _validNetmask(config.netmask);
    const gatewayValid = _validIp(config.gateway);
    const dnsValid = _validIp(config.dns);
    const allValid = ipValid && netmaskValid && gatewayValid && dnsValid;

    const handleSubmit = useCallback(() => {
        if (!onSubmit || !allValid) return;
        setConfiguring(true);
        Promise.resolve(onSubmit(config))
            .catch(() => {})
            .finally(() => setConfiguring(false));
    }, [onSubmit, config, allValid]);

    const handleGetInfo = useCallback(() => {
        if (!onGetInfo) return;
        setInfoOpen(true);
        setInfoLoading(true);
        setInfoError(null);
        onGetInfo().then((resp) => {
            setAdapters(resp);
        }).catch((err) => {
            setInfoError(err?.message ?? String(err));
        }).finally(() => {
            setInfoLoading(false);
        });
    }, [onGetInfo]);

    return <>
        <Table>
            <Row>
                <Cell halign="right">
                    <Switch label="Advanced" checked={advanced} onChange={() => setAdvanced(!advanced)}/>
                </Cell>
            </Row>
            <Row>
                <Cell span={1}>IP Address</Cell>
                <Cell span={4} halign="right">
                    <InputGroup
                        fill round small
                        placeholder="e.g. 192.168.1.100"
                        intent={!ipValid ? "danger" : undefined}
                        value={config.ip ?? ""}
                        onChange={e => setConfig({...config, ip: e.target.value})}
                    />
                </Cell>
            </Row>
            <Row>
                <Cell span={1}>Netmask</Cell>
                <Cell span={4} halign="right">
                    <InputGroup
                        fill round small
                        placeholder="e.g. 255.255.255.0"
                        intent={!netmaskValid ? "danger" : undefined}
                        value={config.netmask ?? ""}
                        onChange={e => setConfig({...config, netmask: e.target.value})}
                    />
                </Cell>
            </Row>
            {advanced && (
                <Fragment>
                    <Row>
                        <Cell span={1}>Gateway</Cell>
                        <Cell span={4} halign="right">
                            <InputGroup
                                fill round small
                                placeholder="e.g. 192.168.1.1"
                                intent={!gatewayValid ? "danger" : undefined}
                                value={config.gateway ?? ""}
                                onChange={e => setConfig({...config, gateway: e.target.value})}
                            />
                        </Cell>
                    </Row>
                    <Row>
                        <Cell span={1}>DNS</Cell>
                        <Cell span={4}>
                            <InputGroup
                                fill round small
                                placeholder="e.g. 8.8.8.8"
                                intent={!dnsValid ? "danger" : undefined}
                                value={config.dns ?? ""}
                                onChange={e => setConfig({...config, dns: e.target.value})}
                            />
                        </Cell>
                    </Row>
                </Fragment>
            )}
            <Row margin='auto auto 20px auto'></Row>
            <Row>
                <Cell>
                    <Button
                        text={configuring ? "Configuring..." : "Configure"}
                        loading={configuring}
                        disabled={!allValid}
                        fill={true}
                        onClick={handleSubmit}
                    />
                </Cell>
            </Row>
            {onGetInfo && <Row>
                <Cell>
                    <Button
                        text="Network Info"
                        icon="info-sign"
                        fill={true}
                        minimal
                        onClick={handleGetInfo}
                    />
                </Cell>
            </Row>}
        </Table>
        <NetworkInfoDialog
            isOpen={infoOpen}
            onClose={() => setInfoOpen(false)}
            adapters={adapters}
            loading={infoLoading}
            error={infoError}
        />
    </>
}

export {NetworkControl};
